import { useTimeDimensionStore } from './timeDimension'
import { useFiltersStore } from './filters'
import { useCurrentUserStore } from './currentUser'
import {
  getDataHealthCheckStatus,
  getDataHealthChecksFromMembers,
  getNormalizationChecksFromMembers,
  getNormalizationStatus
} from '~/services/dataQuality'
import {
  DataHealthCheck,
  DataHealthCheckStatus,
  NormalizationCheckStatus,
  type DataHealthChecks,
  type GetDataHealthChecksDto
} from '~/types/dataQuality'
import { type Query } from '~/types/query'
import { NormalizedFieldEntry } from '~/types/sourceValue'
import type { DateRange } from '~/types/timeDimension'
import { GET_DATA_HEALTH_CHECKS_QUERY } from '~/gql/dataQuality/getDataHealthChecksQuery'
import { buildObjectFromEntries } from '~/types/utils'

interface State {
  normalizationChecks: {
    [key in NormalizedFieldEntry]?: NormalizationCheckStatus
  }
  dataHealthChecks: { [key in DataHealthCheck]?: DataHealthCheckStatus }
  allDataHealthChecks: GetDataHealthChecksDto[]
}

export const useDataQualityStore = defineStore('dataQuality', {
  state: (): State => ({
    normalizationChecks: {},
    dataHealthChecks: {},
    allDataHealthChecks: []
  }),
  getters: {
    allDataHealthChecksMap(state: State): DataHealthChecks {
      return buildObjectFromEntries(
        state.allDataHealthChecks.map(healthCheck => [
          healthCheck.check,
          {
            type: healthCheck.type,
            dimension: healthCheck.dimension
          }
        ])
      )
    }
  },
  actions: {
    async loadAllDataHealthChecks() {
      const app = useNuxtApp()
      this.allDataHealthChecks = (
        await app.$apiGqlClient.query({
          query: GET_DATA_HEALTH_CHECKS_QUERY
        })
      ).data.getDataHealthChecks as GetDataHealthChecksDto[]
    },
    emptyDataQualityChecks() {
      this.normalizationChecks = {}
      this.dataHealthChecks = {}
    },
    emptyDataHealthChecks() {
      this.dataHealthChecks = {}
    },
    async launchDataQualityChecks(queries: Query[]) {
      const { selectedPeriod } = useTimeDimensionStore()
      const { isCurrentUserAdmin } = useCurrentUserStore()

      if (!isCurrentUserAdmin || !selectedPeriod) {
        return
      }

      await this.launchNormalizationCheck(queries, [
        selectedPeriod.dateRange[0],
        selectedPeriod.dateRange[1]
      ])
      await this.launchDataHealthChecks(queries)
    },
    async launchNormalizationCheck(queries: Query[], dateRange: DateRange) {
      const normalizationChecksToRun =
        this.getNormalizationChecksFromQueries(queries)

      normalizationChecksToRun.forEach(normalizationCheck => {
        this.normalizationChecks = {
          ...this.normalizationChecks,
          [normalizationCheck]: NormalizationCheckStatus.LOADING
        }
      })

      await Promise.all(
        normalizationChecksToRun.map(async normalizationCheck => {
          const normalizationStatus = await getNormalizationStatus(
            normalizationCheck,
            dateRange
          )

          this.normalizationChecks = {
            ...this.normalizationChecks,
            [normalizationCheck]: normalizationStatus
          }
        })
      )
    },
    getNormalizationChecksFromQueries(queries: Query[]) {
      return Array.from(
        new Set([
          ...queries.flatMap(query =>
            getNormalizationChecksFromMembers(query.measures)
          ),
          ...queries.flatMap(query =>
            getNormalizationChecksFromMembers(query.dimensions)
          )
        ])
      )
    },
    async launchDataHealthChecks(queries: Query[]) {
      const { selectedPeriod } = useTimeDimensionStore()
      if (!selectedPeriod) {
        return
      }

      const { allFilters } = useFiltersStore()

      const dataHealthChecksToRun = Array.from(
        new Set(
          queries.flatMap(query => this.getDataHealthChecksFromQuery(query))
        )
      )

      dataHealthChecksToRun.forEach(dataHealthCheck => {
        this.dataHealthChecks = {
          ...this.dataHealthChecks,
          [dataHealthCheck]: DataHealthCheckStatus.LOADING
        }
      })

      await Promise.all(
        dataHealthChecksToRun.map(async dataHealthCheck => {
          const dataHealthCheckStatus = await getDataHealthCheckStatus(
            dataHealthCheck,
            selectedPeriod.dateRange,
            allFilters
          )

          this.dataHealthChecks = {
            ...this.dataHealthChecks,
            [dataHealthCheck]: dataHealthCheckStatus
          }
        })
      )
    },
    getDataHealthChecksFromQuery(query: Query) {
      return Array.from(
        new Set([
          ...getDataHealthChecksFromMembers(query.measures),
          ...getDataHealthChecksFromMembers(query.dimensions)
        ])
      )
    }
  }
})
